import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Camera, Copy, FloppyDisk, MagnifyingGlass, Plus, Sparkle, Trash, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../lib/api";
import { InvoiceImages, Thumb } from "./InvoiceImages";
import { ItemRow } from "./ItemRow";
import { JubelioPicker } from "./JubelioPicker";
import { CURRENCIES, btnGhost, btnPrimary, copyText, errMsg, fmtCur, fmtIDR, fmtNum, inp, invoiceTSV, toNum, toastErr } from "./util";

const s = (v) => (v == null ? "" : String(v));
const blankItem = () => ({ key: Math.random().toString(36).slice(2), supplier_sku: "", description: "", qty: "", unit_price: "", line_amount: "", review: false, jubelio: null, matchState: "NONE", suggestions: [] });
const fromSaved = (i) => ({ ...blankItem(), supplier_sku: i.supplier_sku, description: i.description, qty: s(i.qty), unit_price: s(i.unit_price), line_amount: s(i.line_amount), lineManual: false, review: i.review, jubelio: i.jubelio, matchState: i.jubelio ? "SAVED" : "NONE", source: i.source || "MANUAL", line_no: i.ocr_line_no, raw_line: i.ocr_raw_line || "", note: i.note || "" });
const HEADER = { invoice_date: "", invoice_number: "", supplier_name: "", currency: "CNY", exchange_rate: "", total_qty: "", printed_total: "", final_paid: "", adjustment_notes: "", notes: "" };

function Field({ label, warn, children }) {
    return <label className={`block text-[10px] tracking-wide min-w-0 ${warn ? "text-amber-700" : "text-neutral-400"}`}>{label}{warn && " · review"}{children}</label>;
}

function useOcr(onDraft) {
    const [state, setState] = useState(null);
    const timer = useRef(null);
    useEffect(() => () => clearTimeout(timer.current), []);
    const start = async (fileIds) => {
        setState("RUNNING");
        try {
            const { data: job } = await api.post("/invoices/ocr", { file_ids: fileIds });
            const poll = async (n) => {
                const { data } = await api.get(`/invoices/ocr/${job.id}`);
                if (data.status === "RUNNING" && n < 80) { timer.current = setTimeout(() => poll(n + 1), 3000); return; }
                setState(data.status === "DONE" ? "DONE" : "FAILED");
                if (data.status === "DONE") onDraft(data.draft, job.id);
                else toast.error("AI read failed — enter manually. " + (data.error || ""));
            };
            poll(0);
        } catch (e) { setState("FAILED"); toastErr(e, "AI read failed — enter manually"); }
    };
    return [state, start];
}

function DupBanner({ d, onView, onCancel }) {
    return (
        <div className="border-2 border-rose-300 bg-rose-50 rounded-sm p-3 text-sm space-y-1" data-testid="invoice-duplicate-warning">
            <b className="flex items-center gap-1 text-rose-800"><Warning size={16} /> WARNING — INVOICE ALREADY EXISTS</b>
            <div className="text-xs text-ink">Supplier: <b>{d.supplier_name}</b><br />Invoice No: <b>{d.invoice_number}</b><br />Date: <b>{d.invoice_date}</b></div>
            <div className="flex gap-2 pt-1">
                <button type="button" className={btnPrimary} onClick={onView} data-testid="invoice-duplicate-view">View existing invoice</button>
                <button type="button" className={btnGhost} onClick={onCancel} data-testid="invoice-duplicate-cancel">Cancel</button>
            </div>
        </div>
    );
}

function Ask({ tid, title, children, yes, onYes, onNo, danger }) {
    return (
        <div className={`border rounded-sm p-3 text-sm space-y-2 ${danger ? "border-rose-300 bg-rose-50" : "border-amber-300 bg-amber-50"}`} data-testid={tid}>
            <b>{title}</b>
            <div className="text-xs text-ink">{children}</div>
            <div className="flex gap-2">
                <button type="button" className={btnGhost} onClick={onNo} data-testid={`${tid}-cancel`}>Cancel</button>
                <button type="button" className={`${btnPrimary} ${danger ? "!bg-rose-700" : ""}`} onClick={onYes} data-testid={`${tid}-confirm`}>{yes}</button>
            </div>
        </div>
    );
}

export default function InvoiceEditor({ id, onBack, onSaved, onOpen }) {
    const [h, setH] = useState(HEADER);
    const [items, setItems] = useState([]);
    const [files, setFiles] = useState([]);
    const [saved, setSaved] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [uncertain, setUncertain] = useState([]);
    const [warnings, setWarnings] = useState([]);
    const [ocrJob, setOcrJob] = useState(null);
    const [picker, setPicker] = useState(null);
    const [busy, setBusy] = useState(false);
    const [dup, setDup] = useState(null);
    const [askOcr, setAskOcr] = useState(false);
    const [askDel, setAskDel] = useState(false);
    const checkDup = async (supplier, number) => {
        if (!supplier?.trim() || !number?.trim()) return setDup(null);
        try { setDup((await api.get("/invoices/duplicate", { params: { supplier, number, exclude: saved?.id || (id !== "new" ? id : undefined) } })).data.duplicate); } catch { /* save re-checks */ }
    };
    const rate = h.currency === "IDR" ? 1 : toNum(h.exchange_rate) || 0;
    const itemsRef = useRef(items);
    itemsRef.current = items;
    const hRef = useRef(h);
    hRef.current = h;
    const setHead = (k) => (e) => { setH({ ...h, [k]: e.target.value }); setDirty(true); };
    const patchItem = (n, patch) => { setItems((xs) => xs.map((x, i) => (i === n ? { ...x, ...patch } : x))); setDirty(true); };

    const runMatch = async (rows, supplier) => {
        const sent = rows.map((r) => ({ key: r.key, sku: r.supplier_sku.trim() }));
        if (!sent.length) return;
        try {
            const { data } = await api.post("/invoices/match", { supplier_name: supplier, rows: sent });
            const byKey = Object.fromEntries(data.map((m) => [m.key, m]));
            setItems((xs) => xs.map((x) => {
                const m = byKey[x.key];
                if (!m || m.sku !== x.supplier_sku.trim()) return x;
                if (x.jubelio) return m.state === "CONFIRMED" && m.product.item_code === x.jubelio.item_code ? { ...x, jubelio: { ...x.jubelio, stock: m.product.stock, thumbnail: m.product.thumbnail || x.jubelio.thumbnail, catalog_image: m.product.catalog_image || x.jubelio.catalog_image } } : x;
                if (m.state === "CONFIRMED") return { ...x, jubelio: m.product, matchState: "CONFIRMED", suggestions: [] };
                return { ...x, suggestions: m.suggestions || [] };
            }));
        } catch (e) { toastErr(e, "Jubelio matching failed"); }
    };

    useEffect(() => {
        if (id === "new") { return; }
        api.get(`/invoices/${id}`).then(({ data }) => {
            const its = data.items.map(fromSaved);
            setSaved(data);
            setH({ ...HEADER, ...Object.fromEntries(Object.keys(HEADER).map((k) => [k, s(data[k])])) });
            setFiles(data.files.map((f) => f.id));
            setItems(its);
            runMatch(its, data.supplier_name);
        }).catch((e) => toastErr(e, "Invoice not found"));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const [ocrState, startOcr] = useOcr(async (_d, jobId) => {
        setOcrJob(jobId);
        const existing = itemsRef.current.filter((x) => x.supplier_sku || x.qty || x.unit_price);
        let res;
        try {
            res = (await api.post(`/invoices/ocr/${jobId}/rows`, { existing: existing.map((x) => ({ supplier_sku: x.supplier_sku, qty: toNum(x.qty), unit_price: toNum(x.unit_price) })) })).data;
        } catch (e) { return toastErr(e, "OCR result not ready"); }
        const d = res.header;
        const cur = hRef.current, nh = { ...cur };
        ["invoice_date", "invoice_number", "supplier_name", "total_qty", "printed_total"].forEach((k) => { if (!nh[k] && d[k] != null && d[k] !== "") nh[k] = s(d[k]); });
        if (d.currency && !cur.invoice_date && !cur.supplier_name) nh.currency = d.currency;
        setH(nh);
        checkDup(nh.supplier_name, nh.invoice_number);
        const rows = res.rows.map((i) => ({ ...blankItem(), supplier_sku: i.supplier_sku, description: i.description, qty: s(i.qty), unit_price: s(i.unit_price), line_amount: s(i.line_amount ?? ""),
            review: i.review, note: i.note, source: "OCR", line_no: i.line_no, raw_line: i.raw_line }));
        setUncertain(d.uncertain_fields || []);
        setWarnings(d.warnings || []);
        setItems((xs) => [...xs.filter((x) => x.supplier_sku || x.qty || x.unit_price), ...rows]);
        setDirty(true);
        toast.success(`AI read: ${rows.length} new rows${res.skipped.length ? `, ${res.skipped.length} skipped` : ""}. Existing rows unchanged.`);
        runMatch(rows, nh.supplier_name);
    });

    const upload = async (e) => {
        const list = Array.from(e.target.files || []);
        e.target.value = "";
        setBusy(true);
        try {
            const ids = [];
            for (const f of list) {
                const fd = new FormData();
                fd.append("file", f);
                ids.push((await api.post("/invoices/files", fd)).data.id);
            }
            setFiles((x) => [...x, ...ids]);
            setDirty(true);
            toast.success("Invoice image uploaded");
        } catch (ex) { toastErr(ex, "Upload failed"); } finally { setBusy(false); }
    };

    const save = async () => {
        if (!h.invoice_date || !h.supplier_name.trim()) return toast.error("Invoice date and supplier are required");
        if (!rate) return toast.error("Exchange rate is required");
        const body = { ...h, exchange_rate: rate, total_qty: toNum(h.total_qty), printed_total: toNum(h.printed_total), final_paid: toNum(h.final_paid), file_ids: files, ocr_job_id: ocrJob,
            items: items.map((i) => ({ supplier_sku: i.supplier_sku, description: i.description, qty: toNum(i.qty) || 0, unit_price: toNum(i.unit_price) || 0, line_amount: toNum(i.line_amount), review: !!i.review, source: i.source || "MANUAL", ocr_line_no: i.line_no ? String(i.line_no) : null, ocr_raw_line: i.raw_line || "", note: i.review ? (i.note || "").slice(0, 500) : "",
                jubelio: i.jubelio ? { item_id: i.jubelio.item_id, item_code: i.jubelio.item_code, name: i.jubelio.name, thumbnail: i.jubelio.thumbnail } : null })) };
        setBusy(true);
        try {
            const { data } = saved ? await api.put(`/invoices/${saved.id}`, body) : await api.post("/invoices", body);
            setSaved(data);
            setDirty(false);
            toast.success("Invoice saved");
            onSaved(data.id);
        } catch (e) {
            if (e?.response?.status === 409 && e.response.data?.detail?.duplicate) setDup(e.response.data.detail.duplicate);
            else toastErr(e, "Save failed");
        } finally { setBusy(false); }
    };
    const del = async () => {
        try { await api.delete(`/invoices/${saved.id}`); toast.success("Invoice deleted"); onBack(); }
        catch (e) { setAskDel(false); toast.error(errMsg(e)); }
    };
    const readAi = () => (items.some((x) => x.supplier_sku || x.qty || x.unit_price) ? setAskOcr(true) : startOcr(files));

    const copy = async () => { await copyText(invoiceTSV(saved)); toast.success("Copied — paste into Google Sheets / Excel"); };
    const qty = items.reduce((a, i) => a + (toNum(i.qty) || 0), 0), total = items.reduce((a, i) => a + (toNum(i.line_amount) || 0), 0);
    const w = (k) => uncertain.includes(k);

    return (
        <div className="space-y-4 pb-4" data-testid="invoice-editor">
            <button type="button" className="text-xs inline-flex items-center gap-1 text-neutral-600" onClick={onBack} data-testid="invoice-back"><ArrowLeft size={14} /> Invoices</button>
            <section className="border border-mist rounded-sm bg-white p-3 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                    {files.map((f, n) => <Thumb key={f} fid={f} onRemove={() => { if (!window.confirm(`Delete invoice page ${n + 1}?${saved ? " Press Save to apply. Product rows are not changed." : ""}`)) return; setFiles(files.filter((x) => x !== f)); setDirty(true); }} />)}
                    <label className={`${btnGhost} cursor-pointer`} data-testid="invoice-upload-label"><Camera size={14} /> {files.length ? "Add page" : "Upload invoice"}
                        <input type="file" accept="image/*" multiple className="hidden" onChange={upload} disabled={busy} data-testid="invoice-upload-input" /></label>
                    <InvoiceImages fileIds={files} />
                </div>
                {files.length > 0 && !askOcr && (
                    <button type="button" className={btnPrimary} onClick={readAi} disabled={ocrState === "RUNNING"} data-testid="invoice-ocr-btn">
                        <Sparkle size={14} /> {ocrState === "RUNNING" ? "Reading invoice…" : "Read with AI"}</button>
                )}
                {askOcr && <Ask tid="invoice-ocr-warning" title="This invoice already contains products." yes="Continue reading" onNo={() => setAskOcr(false)} onYes={() => { setAskOcr(false); startOcr(files); }}>
                    Reading with AI again may add new rows without replacing existing ones. Please review the product list to avoid duplicate entries.</Ask>}
                <p className="text-[11px] text-neutral-500">AI results are a draft — check all values.</p>
                {warnings.length > 0 && <ul className="text-[11px] text-amber-700 list-disc pl-4" data-testid="invoice-ocr-warnings">{warnings.map((x) => <li key={x}>{x}</li>)}</ul>}
            </section>

            <section className="border border-mist rounded-sm bg-white p-3 grid grid-cols-2 gap-2">
                <Field label="Invoice Date *" warn={w("invoice_date")}><input type="date" className={inp} value={h.invoice_date} onChange={setHead("invoice_date")} data-testid="invoice-date" /></Field>
                <Field label="Invoice No." warn={w("invoice_number")}><input className={inp} value={h.invoice_number} onChange={setHead("invoice_number")} onBlur={() => checkDup(h.supplier_name, h.invoice_number)} data-testid="invoice-number" /></Field>
                <div className="col-span-2"><Field label="Supplier *" warn={w("supplier_name")}><input className={inp} value={h.supplier_name} onChange={setHead("supplier_name")} onBlur={() => { runMatch(items, h.supplier_name); checkDup(h.supplier_name, h.invoice_number); }} data-testid="invoice-supplier" /></Field></div>
                {dup && <div className="col-span-2"><DupBanner d={dup} onView={() => { if (!dirty || window.confirm("Leave this invoice? Unsaved changes will be lost.")) onOpen?.(dup.id); }} onCancel={() => setDup(null)} /></div>}
                <Field label="Currency" warn={w("currency")}><select className={inp} value={h.currency} onChange={setHead("currency")} data-testid="invoice-currency">{CURRENCIES.map((c) => <option key={c}>{c}</option>)}</select></Field>
                <Field label="Exchange Rate *"><input className={inp} inputMode="decimal" value={h.currency === "IDR" ? "1" : h.exchange_rate} disabled={h.currency === "IDR"} onChange={setHead("exchange_rate")} placeholder="e.g. 2350" data-testid="invoice-rate" /></Field>
                <Field label="Printed Qty" warn={w("total_qty")}><input className={inp} inputMode="decimal" value={h.total_qty} onChange={setHead("total_qty")} data-testid="invoice-total-qty" /></Field>
                <Field label="Invoice Total" warn={w("printed_total")}><input className={inp} inputMode="decimal" value={h.printed_total} onChange={setHead("printed_total")} data-testid="invoice-printed-total" /></Field>
                <Field label="Paid (optional)"><input className={inp} inputMode="decimal" value={h.final_paid} onChange={setHead("final_paid")} placeholder="If different" data-testid="invoice-final-paid" /></Field>
                <Field label="Adjustment"><input className={inp} value={h.adjustment_notes} onChange={setHead("adjustment_notes")} placeholder="Discount, change…" data-testid="invoice-adjustment-notes" /></Field>
                <div className="col-span-2"><Field label="Notes"><input className={inp} value={h.notes} onChange={setHead("notes")} data-testid="invoice-notes" /></Field></div>
            </section>

            <section className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                    <h3 className="font-serif text-lg">Products ({items.length})</h3>
                    <button type="button" className={btnGhost} onClick={() => runMatch(items, h.supplier_name)} data-testid="invoice-match-btn"><MagnifyingGlass size={14} /> Match</button>
                </div>
                {items.map((it, n) => <ItemRow key={it.key} it={it} idx={n} rate={rate} onChange={(p) => patchItem(n, p)} onRemove={() => { setItems(items.filter((_, i) => i !== n)); setDirty(true); }} onPick={() => setPicker(n)} />)}
                <button type="button" className={`${btnGhost} w-full`} onClick={() => setItems([...items, blankItem()])} data-testid="invoice-add-row"><Plus size={14} /> Add Product</button>
            </section>

            <section className="border border-mist rounded-sm bg-cream/40 p-3 text-xs grid grid-cols-2 gap-1" data-testid="invoice-totals">
                <span>Qty</span><b className="text-right" data-testid="invoice-items-qty">{fmtNum(qty)}{toNum(h.total_qty) != null && toNum(h.total_qty) !== qty && <span className="text-amber-700"> (printed {fmtNum(h.total_qty)})</span>}</b>
                <span>Total</span><b className="text-right" data-testid="invoice-items-total">{fmtCur(h.currency, total)}</b>
                <span>Invoice Total</span><b className="text-right">{h.printed_total ? fmtCur(h.currency, h.printed_total) : "—"}{toNum(h.printed_total) != null && Math.abs(toNum(h.printed_total) - total) > 0.01 && <span className="text-amber-700"> ≠ Total</span>}</b>
                <span>Paid</span><b className="text-right">{h.final_paid ? fmtCur(h.currency, h.final_paid) : "—"}</b>
                <span>Total Cost</span><b className="text-right" data-testid="invoice-items-total-idr">{fmtIDR(total * rate)}</b>
            </section>

            <div className="sticky bottom-[68px] md:bottom-0 z-30 bg-white/95 backdrop-blur border border-mist rounded-sm p-2 flex gap-2" data-testid="invoice-actions">
                <button type="button" className={`${btnPrimary} flex-1`} onClick={() => save(false)} disabled={busy} data-testid="invoice-save"><FloppyDisk size={14} /> Save</button>
                <button type="button" className={`${btnGhost} flex-1`} onClick={copy} disabled={!saved || dirty} title={dirty ? "Save first" : ""} data-testid="invoice-copy"><Copy size={14} /> Copy</button>
            </div>
            {saved?.updated_at && <p className="text-[11px] text-neutral-400" data-testid="invoice-updated-at">Last updated {new Date(saved.updated_at).toLocaleString("id-ID")} by {saved.updated_by}</p>}
            {saved && !askDel && <button type="button" className="text-xs text-rose-700 inline-flex items-center gap-1 underline" onClick={() => setAskDel(true)} data-testid="invoice-delete"><Trash size={14} /> Delete invoice (Owner)</button>}
            {askDel && <Ask danger tid="invoice-delete-confirm" title="Delete this entire invoice?" yes="Delete invoice" onNo={() => setAskDel(false)} onYes={del}>
                Supplier: <b>{saved.supplier_name}</b> · Invoice No: <b>{saved.invoice_number || "—"}</b> · Date: <b>{saved.invoice_date}</b><br />
                Its {saved.items?.length || 0} product rows and their Price History entries are removed. Catalog products, supplier SKU mappings and other invoices are not changed. A copy is kept in the audit log.</Ask>}
            <JubelioPicker open={picker != null} initial={picker != null ? items[picker]?.description || items[picker]?.supplier_sku || "" : ""} key={picker}
                onClose={() => setPicker(null)} onPick={(p) => patchItem(picker, { jubelio: p, matchState: "OWNER", suggestions: [] })} />
        </div>
    );
}
